import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEPARTMENTS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowUp, ArrowDown, Trash, Plus, Check, PencilSimple, X } from "@phosphor-icons/react";

const COMMON = [
  { key: "contract", label: "Contract" }, { key: "oferta", label: "Ofertă" },
  { key: "proforma", label: "Proformă" }, { key: "factura", label: "Factură" },
  { key: "comanda", label: "Comandă" }, { key: "aviz", label: "Aviz" },
  { key: "cmr", label: "CMR" }, { key: "desen", label: "Desen tehnic" },
  { key: "fisa_proiect", label: "Fișă proiect" }, { key: "pv", label: "Proces-verbal" },
  { key: "certificat", label: "Certificat" }, { key: "dovada", label: "Dovadă încasare" },
  { key: "nota_santier", label: "Notă șantier" },
];

export default function DocTypesAdmin() {
  const { user, hasRole } = useAuth();
  const userDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
  const deptOptions = useMemo(() => (hasRole("admin") ? DEPARTMENTS : DEPARTMENTS.filter((d) => userDepts.includes(d.key))), [hasRole, userDepts]);
  const [dep, setDep] = useState(deptOptions[0]?.key || "");
  const [types, setTypes] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [enabledCommon, setEnabledCommon] = useState([]);

  const load = (d = dep) => {
    if (!d) return;
    api.get(`/doc-types?department=${d}`).then((r) => setTypes(r.data || [])).catch(() => setTypes([]));
    api.get(`/doc-common?department=${d}`).then((r) => setEnabledCommon(r.data?.enabled || [])).catch(() => setEnabledCommon([]));
  };
  const toggleCommon = async (key) => {
    const next = enabledCommon.includes(key) ? enabledCommon.filter((k) => k !== key) : [...enabledCommon, key];
    setEnabledCommon(next);
    try { await api.post("/doc-common", { department: dep, keys: next }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };
  useEffect(() => { load(dep); /* eslint-disable-next-line */ }, [dep]);

  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      await api.post("/doc-types", { department: dep, label });
      setNewLabel(""); load();
      toast.success(`Tip adăugat: ${label}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveRename = async (t) => {
    const label = editLabel.trim();
    if (!label) return;
    try {
      await api.put(`/doc-types/${t.id}`, { label });
      setEditId(null); load();
      toast.success("Tip redenumit");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Ștergi tipul „${t.label}" din nomenclatorul departamentului?`)) return;
    try { await api.delete(`/doc-types/${t.id}`); load(); toast.success("Tip șters"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const arr = [...types];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setTypes(arr);
    try { await api.post("/doc-types/reorder", { department: dep, ordered_ids: arr.map((t) => t.id) }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <div className="space-y-6" data-testid="doctypes-admin-page">
      <div>
        <h1 className="font-head text-2xl font-bold">Nomenclatoare tipuri documente</h1>
        <p className="text-sm text-muted-foreground">Fiecare departament își administrează propria listă de tipuri de document. Tipurile unui departament nu sunt vizibile altuia.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Departament:</span>
          <Select value={dep} onValueChange={setDep}>
            <SelectTrigger className="w-56" data-testid="doctypes-dept"><SelectValue placeholder="Alege departamentul" /></SelectTrigger>
            <SelectContent>
              {deptOptions.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mb-5 flex items-center gap-2">
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Tip nou de document (ex: Documentație tehnică)…" className="max-w-md" data-testid="doctypes-new-input" />
          <Button onClick={add} disabled={!dep || !newLabel.trim()} data-testid="doctypes-add-btn"><Plus size={16} className="mr-1" /> Adaugă</Button>
        </div>

        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tipuri specifice departamentului</p>
        <div className="space-y-2" data-testid="doctypes-list">
          {types.map((t, idx) => (
            <div key={t.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid={`doctype-row-${t.id}`}>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`doctype-up-${t.id}`}><ArrowUp size={14} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === types.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`doctype-down-${t.id}`}><ArrowDown size={14} /></button>
              </div>
              {editId === t.id ? (
                <>
                  <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename(t)} className="h-8 max-w-sm" data-testid={`doctype-edit-input-${t.id}`} />
                  <button onClick={() => saveRename(t)} className="text-emerald-400 hover:text-emerald-300" data-testid={`doctype-save-${t.id}`}><Check size={18} /></button>
                  <button onClick={() => setEditId(null)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm">{t.label}</span>
                  <button onClick={() => { setEditId(t.id); setEditLabel(t.label); }} className="text-muted-foreground hover:text-primary" data-testid={`doctype-rename-${t.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => del(t)} className="text-muted-foreground hover:text-red-400" data-testid={`doctype-del-${t.id}`}><Trash size={16} /></button>
                </>
              )}
            </div>
          ))}
          {types.length === 0 && <p className="text-sm text-muted-foreground">Niciun tip specific încă. Adaugă primul tip mai sus.</p>}
        </div>

        <p className="mt-6 mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tipuri comune (bifează ce apare la acest departament)</p>
        <div className="flex flex-wrap gap-1.5" data-testid="doctypes-common">
          {COMMON.map((c) => {
            const on = enabledCommon.includes(c.key);
            return (
              <button key={c.key} onClick={() => toggleCommon(c.key)} className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border bg-white/5 text-muted-foreground hover:text-foreground"}`} data-testid={`doctypes-common-${c.key}`}>
                {on ? "✓ " : ""}{c.label}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground/70">Tipurile comune nu pot fi șterse — doar bifate/debifate pentru acest departament. Cele bifate apar în lista de la „Adaugă documente".</p>
      </div>
    </div>
  );
}
